import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Moon, PanelLeft, Sun, Wifi, WifiOff } from 'lucide-react';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { useTheme } from '@/lib/theme';
import { useAuthStore } from '@/store/auth.store';
import { Logo } from '@/components/ui/Logo';

/*
  The command bar.

  Deliberately the one piece of chrome that does not flip with the theme: it is
  what identifies the product, and a bar that goes white in light mode turns the
  dashboard into a generic admin template. Everything painted on it therefore
  reads `navbar-foreground`, not `primary-foreground` — the latter pairs with
  --primary and does flip, which is how white chrome ends up invisible.

  It also carries the gate connection state, because on this product that is
  operational information rather than decoration: if the socket is down, the
  live board is stale and whoever is watching it needs to know before they
  trust it.
*/

export function AppBar({
  sidebarOpen,
  onToggleSidebar,
  connected,
}: {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  connected?: boolean;
}) {
  const { user, logout } = useAuthStore();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const initial =
    user?.fullName?.[0]?.toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? 'A';

  return (
    <header className="fixed inset-x-0 top-0 z-50 h-bar bg-navbar text-navbar-foreground">
      <div className="flex h-full items-center justify-between">
        {/* ── Left: lockup + rail toggle ─────────────────────────────────── */}
        <div
          className={cn(
            'flex h-full items-center gap-2 px-2.5 transition-[width] duration-200',
            sidebarOpen ? 'min-[900px]:w-rail' : 'min-[900px]:w-rail-collapsed',
          )}
        >
          <Link
            to="/dashboard"
            className="flex flex-shrink-0 items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navbar-foreground/50"
          >
            {/* The reversed cut sits straight on the navy — the mark carries
                its own ground, so a blue plate behind it would be a second
                container around a container. */}
            <Logo size={22} tone="reversed" className="flex-shrink-0" />
            {sidebarOpen && (
              <span className="text-[12.5px] font-bold tracking-tight">SafePass</span>
            )}
          </Link>

          <button
            type="button"
            onClick={onToggleSidebar}
            aria-label={sidebarOpen ? 'Collapse navigation' : 'Expand navigation'}
            aria-expanded={sidebarOpen}
            className="ml-auto flex h-7 w-7 items-center justify-center rounded-md text-navbar-foreground/70 transition-colors hover:bg-navbar-foreground/10 hover:text-navbar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navbar-foreground/50"
          >
            <PanelLeft size={15} />
          </button>
        </div>

        {/* ── Right: status + theme + account ────────────────────────────── */}
        <div className="flex items-center gap-1 px-2.5">
          {connected !== undefined && (
            <span
              title={connected ? 'Live gate feed connected' : 'Live gate feed disconnected — the board may be stale'}
              className={cn(
                'mr-1 hidden items-center gap-1.5 rounded-md border px-2 py-[3px] text-[10px] font-semibold uppercase tracking-[0.06em] sm:inline-flex',
                connected
                  ? 'border-success/40 bg-success/10 text-success'
                  : 'border-warning/40 bg-warning/10 text-warning',
              )}
            >
              {connected ? <Wifi size={11} /> : <WifiOff size={11} />}
              {connected ? 'Live' : 'Offline'}
            </span>
          )}

          <button
            type="button"
            onClick={toggle}
            aria-label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            className="flex h-7 w-7 items-center justify-center rounded-md text-navbar-foreground/70 transition-colors hover:bg-navbar-foreground/10 hover:text-navbar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navbar-foreground/50"
          >
            {resolved === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="flex h-7 items-center gap-1.5 rounded-md pl-1 pr-1.5 transition-colors hover:bg-navbar-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navbar-foreground/50"
            >
              <span className="flex h-[22px] w-[22px] items-center justify-center rounded-md bg-navbar-foreground/15 text-[10.5px] font-bold">
                {initial}
              </span>
              <span className="hidden max-w-[130px] truncate text-[11.5px] font-semibold md:block">
                {user?.fullName ?? user?.email ?? 'Account'}
              </span>
              <ChevronDown size={12} className="text-navbar-foreground/60" />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-[calc(100%+6px)] w-56 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-overlay animate-slide-up"
              >
                <div className="border-b border-border px-3 py-2.5">
                  <p className="truncate text-[12px] font-bold">{user?.fullName ?? 'Account'}</p>
                  <p className="mt-0.5 truncate text-[10.5px] text-muted-foreground">{user?.email}</p>
                  {user?.role && (
                    <p className={cn(styles.label, 'mt-1.5')}>{user.role.replace(/_/g, ' ')}</p>
                  )}
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[11.5px] font-semibold text-[hsl(var(--record-ink-danger))] transition-colors hover:bg-destructive/10"
                >
                  <LogOut size={13} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
